import { useState, useEffect } from "react";
import { Button } from "@app/components/shared/Button";
import { injectTrade, getTradingSystemPayload } from "@app/services/tradingSystemApi";

interface DirectExecutionTabProps {
  onSuccess?: () => void;
}

export function DirectExecutionTab({ onSuccess }: DirectExecutionTabProps): JSX.Element {
  const [formData, setFormData] = useState({
    symbol: "",
    side: "buy" as "buy" | "sell",
    entry_price: "",
    stop_loss: "",
    take_profit: [""],
    confidence: "0.8",
    source: "manual_injection",
    summary: "",
    risk_reward_ratio: "",
    entry_min: "",
    entry_max: "",
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingPrice, setIsLoadingPrice] = useState(false);
  const [isLoadingTP, setIsLoadingTP] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [tpLayers, setTpLayers] = useState<number[] | null>(null);

  // Auto-calculate TP prices when entry_price changes and tpLayers are loaded
  useEffect(() => {
    if (tpLayers && tpLayers.length >= 2 && formData.entry_price && parseFloat(formData.entry_price) > 0) {
      const entryPrice = parseFloat(formData.entry_price);
      const tpPrices = tpLayers.map((layer) => entryPrice * (1 + layer / 100));
      setFormData((prev) => ({
        ...prev,
        take_profit: tpPrices.map((tp) => tp.toFixed(8)),
      }));
    }
  }, [formData.entry_price, tpLayers]);

  function handleInputChange(field: string, value: string | number | string[]): void {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setError(null);
    setSuccess(null);
  }

  function handleAddTakeProfit(): void {
    setFormData((prev) => ({
      ...prev,
      take_profit: [...prev.take_profit, ""],
    }));
  }

  function handleRemoveTakeProfit(index: number): void {
    if (formData.take_profit.length > 1) {
      setFormData((prev) => ({
        ...prev,
        take_profit: prev.take_profit.filter((_, i) => i !== index),
      }));
    }
  }

  function handleTakeProfitChange(index: number, value: string): void {
    setFormData((prev) => ({
      ...prev,
      take_profit: prev.take_profit.map((tp, i) => (i === index ? value : tp)),
    }));
  }

  async function loadTpLayersFromSettings(): Promise<void> {
    setIsLoadingTP(true);
    setError(null);
    try {
      const payload = await getTradingSystemPayload();
      const settings = payload.settings as unknown as { tp_layers: number[] };
      if (settings && settings.tp_layers && settings.tp_layers.length >= 2) {
        const layers = settings.tp_layers.slice(0, 2); // System uses 2 TP levels only
        setTpLayers(layers);
        
        // Calculate TP prices if entry_price is available
        if (formData.entry_price && parseFloat(formData.entry_price) > 0) {
          const entryPrice = parseFloat(formData.entry_price);
          const tpPrices = layers.map((layer) => entryPrice * (1 + layer / 100));
          setFormData((prev) => ({
            ...prev,
            take_profit: tpPrices.map((tp) => tp.toFixed(8)),
          }));
          setSuccess(`Loaded TP layers from settings: ${layers.join("%, ")}%. Calculated TP prices based on entry price.`);
        } else {
          setSuccess(`Loaded TP layers from settings: ${layers.join("%, ")}%. Enter entry price to calculate TP prices automatically.`);
        }
      } else {
        throw new Error("TP layers not found in settings. Please configure TP layers in Trading Settings first.");
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load TP layers from settings";
      setError(message);
    } finally {
      setIsLoadingTP(false);
    }
  }

  async function getCurrentMarketPrice(): Promise<void> {
    if (!formData.symbol.trim()) {
      setError("Please enter symbol first");
      return;
    }
    
    setIsLoadingPrice(true);
    setError(null);
    try {
      // The backend will fetch current price automatically if entry_price is not provided
      // But we can show a message that it will be fetched automatically
      setSuccess("Current market price will be fetched automatically when you submit. You can also enter it manually.");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to get current price";
      setError(message);
    } finally {
      setIsLoadingPrice(false);
    }
  }

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setIsSubmitting(true);

    try {
      // Validate required fields
      if (!formData.symbol.trim()) {
        throw new Error("Symbol is required");
      }
      
      // Prepare request - all fields are optional now (backend will calculate if missing)
      const request: any = {
        symbol: formData.symbol.trim(),
        side: formData.side,
        confidence: parseFloat(formData.confidence) || 0.8,
        source: formData.source || "manual_injection",
      };
      
      // Add optional fields only if provided
      if (formData.entry_price && parseFloat(formData.entry_price) > 0) {
        request.entry_price = parseFloat(formData.entry_price);
      }
      
      if (formData.stop_loss && parseFloat(formData.stop_loss) > 0) {
        request.stop_loss = parseFloat(formData.stop_loss);
      }
      
      const validTakeProfits = formData.take_profit
        .map((tp) => tp.trim())
        .filter((tp) => tp && parseFloat(tp) > 0)
        .map((tp) => parseFloat(tp));
      if (validTakeProfits.length > 0) {
        request.take_profit = validTakeProfits;
      }
      
      if (formData.summary.trim()) {
        request.summary = formData.summary.trim();
      }
      
      if (formData.risk_reward_ratio && parseFloat(formData.risk_reward_ratio) > 0) {
        request.risk_reward_ratio = parseFloat(formData.risk_reward_ratio);
      }
      
      if (formData.entry_min && parseFloat(formData.entry_min) > 0) {
        request.entry_min = parseFloat(formData.entry_min);
      }
      
      if (formData.entry_max && parseFloat(formData.entry_max) > 0) {
        request.entry_max = parseFloat(formData.entry_max);
      }

      const result = await injectTrade(request);

      setSuccess(
        `Trade injected successfully! Signal ID: ${result.signal_id}. Recommendation ID: ${result.recommendation_id}`
      );
      
      // Reset form
      setFormData({
        symbol: "",
        side: "buy",
        entry_price: "",
        stop_loss: "",
        take_profit: [""],
        confidence: "0.8",
        source: "manual_injection",
        summary: "",
        risk_reward_ratio: "",
        entry_min: "",
        entry_max: "",
      });

      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to inject trade";
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="configuration-section">
        <div className="configuration-grid">
          <div className="config-item">
            <label htmlFor="symbol">
              <strong>Symbol *</strong>
            </label>
            <input
              id="symbol"
              type="text"
              placeholder="BTCUSDT or BTC/USDT"
              value={formData.symbol}
              onChange={(e) => handleInputChange("symbol", e.target.value.toUpperCase())}
              required
            />
            <small style={{ color: "var(--color-text-secondary)" }}>
              Trading symbol (e.g., BTCUSDT, ETH/USDT)
            </small>
          </div>

          <div className="config-item">
            <label htmlFor="side">
              <strong>Side *</strong>
            </label>
            <select
              id="side"
              value={formData.side}
              onChange={(e) => handleInputChange("side", e.target.value as "buy" | "sell")}
              required
            >
              <option value="buy">Buy (Long)</option>
              <option value="sell">Sell (Short)</option>
            </select>
            <small style={{ color: "var(--color-text-secondary)" }}>
              Buy for long position, Sell for short position
            </small>
          </div>

          <div className="config-item">
            <label htmlFor="entry_price">
              <strong>Entry Price</strong>
              <small style={{ marginLeft: "8px", color: "var(--color-text-secondary)" }}>
                (Optional - will use current market price if not provided)
              </small>
            </label>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <input
                id="entry_price"
                type="number"
                step="any"
                placeholder="50000.0 (or leave empty for current price)"
                value={formData.entry_price}
                onChange={(e) => handleInputChange("entry_price", e.target.value)}
                style={{ flex: 1 }}
              />
              <Button
                type="button"
                variant="ghost"
                size="small"
                onClick={getCurrentMarketPrice}
                disabled={isLoadingPrice || !formData.symbol.trim()}
                title="Current price will be fetched automatically on submit"
              >
                Auto
              </Button>
            </div>
          </div>

          <div className="config-item">
            <label htmlFor="stop_loss">
              <strong>Stop Loss</strong>
              <small style={{ marginLeft: "8px", color: "var(--color-text-secondary)" }}>
                (Optional - will be calculated from entry price if not provided)
              </small>
            </label>
            <input
              id="stop_loss"
              type="number"
              step="any"
              placeholder="49000.0 (or leave empty for auto calculation)"
              value={formData.stop_loss}
              onChange={(e) => handleInputChange("stop_loss", e.target.value)}
            />
          </div>

          <div className="config-item" style={{ gridColumn: "1 / -1" }}>
            <label>
              <strong>Take Profit Levels</strong>
              <small style={{ marginLeft: "8px", color: "var(--color-text-secondary)" }}>
                (Optional - will be calculated from TP layers settings if not provided)
              </small>
              <div style={{ display: "flex", gap: "8px", marginTop: "4px" }}>
                <Button
                  type="button"
                  variant="ghost"
                  size="small"
                  onClick={loadTpLayersFromSettings}
                  disabled={isLoadingTP}
                  title="Load TP layers from Trading Settings and calculate TP prices"
                >
                  {isLoadingTP ? "Loading..." : "Load from Settings"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="small"
                  onClick={handleAddTakeProfit}
                >
                  + Add Manual
                </Button>
              </div>
            </label>
            {tpLayers && (
              <div style={{ marginBottom: "8px", padding: "8px", background: "var(--color-background-subtle)", borderRadius: "4px" }}>
                <small style={{ color: "var(--color-text-secondary)" }}>
                  TP Layers from Settings: {tpLayers.join("%, ")}%
                </small>
              </div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {formData.take_profit.map((tp, index) => (
                <div key={index} style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <input
                    type="number"
                    step="any"
                    placeholder={`TP ${index + 1} (or leave empty for auto calculation)`}
                    value={tp}
                    onChange={(e) => handleTakeProfitChange(index, e.target.value)}
                    style={{ flex: 1 }}
                  />
                  {formData.take_profit.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="small"
                      onClick={() => handleRemoveTakeProfit(index)}
                    >
                      Remove
                    </Button>
                  )}
                </div>
              ))}
            </div>
            <small style={{ color: "var(--color-text-secondary)" }}>
              Leave empty to use TP layers from settings, or enter manually
            </small>
          </div>

          <div className="config-item">
            <label htmlFor="confidence">
              <strong>Confidence</strong>
            </label>
            <input
              id="confidence"
              type="number"
              step="0.01"
              min="0"
              max="1"
              placeholder="0.8"
              value={formData.confidence}
              onChange={(e) => handleInputChange("confidence", e.target.value)}
            />
            <small style={{ color: "var(--color-text-secondary)" }}>
              Confidence score (0.0-1.0), default: 0.8
            </small>
          </div>

          <div className="config-item">
            <label htmlFor="source">
              <strong>Source</strong>
            </label>
            <input
              id="source"
              type="text"
              placeholder="manual_injection"
              value={formData.source}
              onChange={(e) => handleInputChange("source", e.target.value)}
            />
          </div>

          <div className="config-item" style={{ gridColumn: "1 / -1" }}>
            <label htmlFor="summary">
              <strong>Summary (Optional)</strong>
            </label>
            <textarea
              id="summary"
              rows={2}
              placeholder="Optional description or notes"
              value={formData.summary}
              onChange={(e) => handleInputChange("summary", e.target.value)}
            />
          </div>

          <div className="config-item">
            <label htmlFor="risk_reward_ratio">
              <strong>Risk/Reward Ratio (Optional)</strong>
            </label>
            <input
              id="risk_reward_ratio"
              type="number"
              step="any"
              placeholder="2.0"
              value={formData.risk_reward_ratio}
              onChange={(e) => handleInputChange("risk_reward_ratio", e.target.value)}
            />
          </div>

          <div className="config-item">
            <label htmlFor="entry_min">
              <strong>Entry Min (Optional)</strong>
            </label>
            <input
              id="entry_min"
              type="number"
              step="any"
              placeholder="49900.0"
              value={formData.entry_min}
              onChange={(e) => handleInputChange("entry_min", e.target.value)}
            />
          </div>

          <div className="config-item">
            <label htmlFor="entry_max">
              <strong>Entry Max (Optional)</strong>
            </label>
            <input
              id="entry_max"
              type="number"
              step="any"
              placeholder="50100.0"
              value={formData.entry_max}
              onChange={(e) => handleInputChange("entry_max", e.target.value)}
            />
          </div>
        </div>

        {error && (
          <div className="empty-state" style={{ color: "var(--color-error)", marginTop: "16px" }}>
            {error}
          </div>
        )}

        {success && (
          <div
            className="empty-state"
            style={{ color: "var(--color-success)", marginTop: "16px" }}
          >
            {success}
          </div>
        )}

        <div className="config-actions" style={{ marginTop: "24px" }}>
          <Button type="submit" variant="primary" disabled={isSubmitting}>
            {isSubmitting ? "Injecting..." : "Inject Trade"}
          </Button>
        </div>
      </div>
    </form>
  );
}

